import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, Plus } from "lucide-react";
import { useAgents } from "@/contexts/AgentContext";
import VoiceAgentCard from "@/components/voice/VoiceAgentCard";
import { AgentCardSkeleton } from "@/components/ui/skeleton";
import { VoiceAgent, VoiceAgentStats } from "@/services/voiceAgentService";

interface VoiceAgentsListProps {
  onCreateAgent: () => void;
  agents?: VoiceAgent[];
  isLoading?: boolean;
  agentStats?: Record<string, VoiceAgentStats>;
}

export default function VoiceAgentsList({
  onCreateAgent,
  agents: propAgents,
  isLoading: propIsLoading,
  agentStats: propAgentStats,
}: VoiceAgentsListProps) {
  const navigate = useNavigate();
  const {
    activeVoiceAgents,
    isLoadingVoiceAgents,
    voiceAgentStats,
    refreshVoiceAgents,
  } = useAgents();

  // Use props if provided, otherwise fall back to context
  const agents = propAgents ?? activeVoiceAgents;
  const isLoading = propIsLoading ?? isLoadingVoiceAgents;
  const agentStats = propAgentStats ?? voiceAgentStats;

  // Fix race condition: prevent flash of empty state by using a brief delay
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading && agents.length === 0 && isInitialLoad) {
      // Small delay to prevent race condition flash
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 50);
      return () => clearTimeout(timer);
    } else if (agents.length > 0) {
      setIsInitialLoad(false);
    } else if (isLoading) {
      setIsInitialLoad(true);
    }
  }, [isLoading, agents.length, isInitialLoad]);

  // Function to get gradient color based on agent ID
  const getAgentGradient = (id: string): string => {
    const colors = [
      "from-blue-100 to-blue-300",
      "from-purple-100 to-purple-300",
      "from-pink-100 to-pink-300",
      "from-green-100 to-green-300",
      "from-amber-100 to-amber-300",
      "from-cyan-100 to-cyan-300",
      "from-indigo-100 to-indigo-300",
      "from-rose-100 to-rose-300",
      "from-emerald-100 to-emerald-300",
      "from-orange-100 to-orange-300",
    ];

    const index =
      id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;
    return `bg-gradient-to-br ${colors[index]}`;
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleNavigateToDetail = (id: string) => {
    navigate(`/voice-agents/${id}`);
  };

  const handleViewCalls = (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    navigate(`/voice-calls?agent=${agentId}`);
  };

  const handleAgentUpdate = async (_updated?: VoiceAgent) => {
    try {
      await refreshVoiceAgents();
    } catch (e) {
      // best-effort refresh; ignore errors to avoid blocking UI
    }
  };

  const shouldShowLoading = isLoading || (isInitialLoad && agents.length === 0);

  if (shouldShowLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[...Array(4)].map((_, i) => (
          <AgentCardSkeleton key={i} showPhoneStatus={true} />
        ))}
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <Card className="bg-muted/50">
        <CardContent className="py-12 flex flex-col items-center justify-center">
          <Phone className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No voice agents yet</h3>
          <p className="text-muted-foreground mb-6 text-center max-w-md">
            Create your first voice AI agent to handle phone calls and provide
            voice-based customer support.
          </p>
          <Button onClick={onCreateAgent}>
            <Plus className="mr-2 h-4 w-4" />
            Create Voice Agent
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {agents.map((agent) => {
        const stats = agentStats[agent.id] || {
          total_calls: 0,
          calls_today: 0,
          average_duration_seconds: 0,
          end_reasons: {},
          total_cost: 0,
          last_30_days: 0,
          success_rate: 0,
        };

        return (
          <VoiceAgentCard
            key={agent.id}
            agent={agent}
            stats={stats}
            onNavigateToDetail={handleNavigateToDetail}
            onViewCalls={handleViewCalls}
            onAgentUpdate={handleAgentUpdate}
            showPhoneStatus={true}
          />
        );
      })}
    </div>
  );
}
